import React, { useEffect, useState } from 'react';
import { getActiveEvents } from '../services/event.service';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import Header from '../components/HeaderThree';
import ScrollToTop from "react-scroll-to-top";
import Preloader from "../helper/Preloader";
import ColorInit from "../helper/ColorInit";
import NewsletterTwo from "../components/NewsletterTwo";

const EventsPage = () => {
    const [events, setEvents] = useState([]);
    const [filter, setFilter] = useState('ALL');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const data = await getActiveEvents();
                setEvents(data);
            } catch (error) {
                console.error("Error fetching events:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchEvents();
    }, []);

    const filteredEvents = events.filter(event => {
        if (filter === 'ALL') return true;
        return event.type === filter;
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            weekday: 'short',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            <style>{`
                @media (min-width: 576px) {
                    .event-card-half {
                        width: 50% !important;
                        flex: 0 0 50% !important;
                    }
                }
                @media (max-width: 575px) {
                    .event-card-half {
                        width: 100% !important;
                    }
                    .event-card-img-wrap {
                        height: 240px !important;
                    }
                }
            `}</style>
            <ColorInit color={true} />
            <Preloader />
            <ScrollToTop smooth color="var(--brand-primary)" />
            <Header />
            <Breadcrumb title="Artform Workshops" />

            <section className="events-section py-80">
                <div className="container">
                    <div className="section-heading mb-48">
                        <h2 className="mb-12 text-center">Upcoming Workshops</h2>
                        <p className="text-gray-600 max-w-800 mx-auto leading-relaxed text-justify" style={{ textAlign: 'justify' }}>
                            Join us at Vedha’s Culture for an immersive journey into the world of handmade artistry. 
                            Discover and participate in our curated selection of online and offline creative workshops, professional training sessions, and inspiring exhibitions. 
                            Whether you're a beginner looking to explore a new hobby or an enthusiast aiming to perfect your craft, 
                            our workshops offer the perfect blend of tradition, creativity, and joyful learning.
                        </p>
                    </div>

                    {/* Filters */}
                    <div className="flex-center flex-wrap gap-12 mb-48">
                        {['ALL', 'ONLINE', 'OFFLINE'].map((type) => (
                            <button
                                key={type}
                                onClick={() => setFilter(type)}
                                className={`px-24 py-10 rounded-pill fw-bold text-sm transition-2 ${filter === type
                                    ? 'bg-main-600 text-white shadow-md'
                                    : 'bg-white text-gray-600 border border-gray-100 hover-bg-main-50'
                                    }`}
                            >
                                {type.charAt(0) + type.slice(1).toLowerCase()}
                            </button>
                        ))}
                    </div>

                    {loading ? (
                        <div className="text-center py-80">
                            <div className="spinner-border text-main-600" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : filteredEvents.length === 0 ? (
                        <div className="text-center py-80 bg-white rounded-24 border border-gray-100 shadow-sm">
                            <i className="ph ph-calendar-x text-64 text-gray-200 mb-16"></i>
                            <h4 className="text-gray-400">No workshops found for this category.</h4>
                        </div>
                    ) : (
                        <div className="row gy-4 justify-content-center">
                            {filteredEvents.map((event) => (
                                <div key={event.id} className="col-lg-6">
                                    <div className="event-card bg-white rounded-24 border border-gray-100 overflow-hidden hover-shadow-lg transition-3 d-flex flex-column flex-sm-row h-100 group">
                                        <div className="event-card-half event-card-img-wrap position-relative overflow-hidden shrink-0 d-flex align-items-stretch">
                                            <Link to={`/workshop/${event.id}`} className="w-100 d-block">
                                                <img
                                                    src={event.imageUrl}
                                                    alt={event.title}
                                                    className="w-100 h-100 object-fit-cover transition-scale group-hover-scale-110"
                                                />
                                            </Link>
                                        </div>
                                        <div className="p-24 flex-grow-1 d-flex flex-column event-card-half">
                                            <div className="mb-8 d-flex align-items-center gap-8 text-main-600 fw-bold text-xs uppercase tracking-wider">
                                                <i className="ph-fill ph-calendar"></i>
                                                {formatDate(event.startAt).split(',')[0]}
                                            </div>
                                            <Link to={`/workshop/${event.id}`} className="text-gray-900 hover-text-main-600 transition-2">
                                                <h5 className="mb-12 fw-bold line-clamp-2">{event.title}</h5>
                                            </Link>
                                            <div className="space-y-8 mb-20">
                                                <div className="d-flex align-items-center gap-8 text-gray-500 text-sm">
                                                    <i className="ph ph-clock text-main-600"></i>
                                                    {formatDate(event.startAt).split(',')[1]}
                                                </div>
                                                <div className="d-flex align-items-center gap-8 text-gray-500 text-sm">
                                                    <i className="ph ph-map-pin text-main-600"></i>
                                                    <span className="truncate">{event.type === 'ONLINE' ? 'Virtual Meeting' : (event.venue || 'TBA')}</span>
                                                </div>
                                            </div>
                                            <p className="text-gray-400 text-sm mb-24 line-clamp-4">
                                                {event.description || "Join us for a unique artform experience where tradition meets creativity."}
                                            </p>
                                            <div className="mt-auto">
                                                <Link
                                                    to={`/workshop/${event.id}`}
                                                    className="btn bg-main-600 text-white rounded-pill w-100 py-12 text-sm fw-bold border border-main-100 transition-2"
                                                >
                                                    BOOK NOW
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* NewsletterTwo */}
      <NewsletterTwo />

            <Footer />
        </>
    );
};

export default EventsPage;